import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import ActivityService from "../../../services/ActivityService";
import ClientService, { ClientFields, getClientFieldDisplayValue } from "../../../services/ClientService";
import CompanyService, { DataType, Entities } from "../../../services/CompanyService";
import GroupService from "../../../services/GroupService";
import ToastContext from "../../shared/bootstrap/Toast";
import Dropdown, { DropdownItems } from "../../shared/components/Dropdown";
import { AutocompleteSelect, TagsDropdownEditor, TextEditor } from "../../shared/components/Editors";
import FloatingPanelService from "../../shared/FloatingPanel";
import { TranslationService } from "../../../services/TranslationService";
import { FloatingPanelFooter } from "../../shared/components/FloatingPanelFooter";
import TagService from "../../../services/TagService";
import { Client } from "../entities/ClientGetResponse";
import { Cast, hasValuesLength } from "../../../utils/Utils";
import TooltipComponent from "../../shared/TooltipComponent";
import { CacheEntity, StorageService } from "../../../services/StorageService";
import { EntityField } from "../../shared/entities/EntityField";
import { RequiredManager, ValidationMessage } from "../../shared/RequieredManager";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import { Editor as SMSEditor } from "react-draft-wysiwyg";
import draftToHtml from "draftjs-to-html";
import htmlToDraft from "html-to-draftjs";
import { ContentState, convertToRaw, EditorState, Modifier } from "draft-js";
import { EditorType } from "../../company/Configuration/EmailTemplateEdit";

class SMSEdit {
    groupId?: string;
    telephone?: string;
    message?: string;
    activityTypeId?: number;
    tags?: string;

    constructor(person: Client, activityTypeId: number | undefined, groupId?: string) {
        this.activityTypeId = activityTypeId ?? CompanyService.getActivityTypes().find(x => x.SMS && x.SMSDefault)?.ActivityTypeID;
        const field = CompanyService.getCompanyAuth()?.DefaultSMSField;
        if (field) {
            this.telephone = field === -1 ? person.Phone : person.additionals.find(x => x.id === CompanyService.getCompanyAuth()!.DefaultSMSField)?.value;
        }
        this.groupId = groupId;
    }
}

export const SMSCompose = ({ person, onSubmit, groupId }: { person: Client, onSubmit?: () => void, groupId?: number }) => {
    const activityTypeOptions = CompanyService.getActivityTypes().filter(x => x.SMS).map(x => ({ value: x.ActivityTypeID, text: x.ActivityTypeName }));

    const [SMS, setSMS] = useState(new SMSEdit(person, CompanyService.getActivityTypes().find(x => x.SMS && x.SMSDefault)?.ActivityTypeID));
    const [isSending, setIsSending] = useState(false);
    const [messageCharCount, setMessageCharCount] = useState(0);
    const { translate } = TranslationService;
    const { showToast } = useContext(ToastContext);
    const validationFunctionArray: (() => boolean)[] = [];
    const maxCharCount = 160;
    const groupLabel = groupId && StorageService.getCache(CacheEntity.GroupName, groupId.toString());
    const setSMSField = useCallback((property: string) => (value: number | string | undefined) => {
        setSMS(SMS => ({ ...SMS, [property]: value }));
    }, [setSMS]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const requiredManager = new RequiredManager();
    const changeBody = requiredManager.makeRequiredWithId((x: string) => {
        setSMSField("message")(x);
    }, "message");

    const getDefaultState = () => {
        const blocksFromHtml = htmlToDraft(SMS.message ?? "");
        const { contentBlocks, entityMap } = blocksFromHtml;
        const contentState = ContentState.createFromBlockArray(contentBlocks, entityMap);
        convertToRaw(contentState);
        return EditorState.createEmpty();
    };
    const currentEditorState = useRef(getDefaultState());
    const editorRef = useRef<null | EditorType>(null);

    const reqTelephone = requiredManager.makeRequired(setSMSField("telephone"));

    useEffect(() => {
        if (SMS.telephone === undefined) {
            ClientService.get({ id: person.PersonId.toString() }).then(x => {
                if (x instanceof Error) { return; }
                setSMSField("telephone")(x.item.Phone);
            });
        }
    }, [SMS.telephone, person.PersonId, setSMSField]);

    useEffect(() => {
        setSMSField("groupId")(groupId);
    }, [groupId, setSMSField]);

    const PhoneFieldItems = useMemo(() => {
        if (person?.PersonId) {
            return [
                ...CompanyService.getAdditionalDefinitionsFiltered(Entities.Client, false).map(x => new EntityField(x)),
                ...CompanyService.getConfigItemsFiltered(Entities.Client, true, [ClientFields.Name, ClientFields.Contact, ClientFields.Email, ClientFields.Phone, ClientFields.Address, ClientFields.Comments]).map(x => new EntityField(x)),
            ]
                .filter(x => x.type === DataType.Phone || x.type === DataType.PhoneWhatsapp)
                .map(x => {
                    const value = getClientFieldDisplayValue(x, person);
                    return ({ text: x.title + ' - ' + value, value })
                });
        }
        return [];
    }, [person]);

    const PhoneItems = useMemo(() => [
        { text: TranslationService.translate.SelectAPHone, value: undefined },
        ...PhoneFieldItems.filter(el => el.value)
    ], [PhoneFieldItems]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const canSubmit = () => validationFunctionArray.filter(x => !x()).length === 0;
    const submit = useCallback(
        async () => {
            if (!requiredManager.validate()) {
                return;
            }
            if (isSending || !canSubmit() || !SMS.telephone?.length) { return; }
            setIsSending(true);

            {
                const result = await TagService.createMissingTags(SMS.tags);
                if (result instanceof Error) {
                    showToast(translate.ErrorProcessingRequest, undefined, "danger");
                    setIsSending(false);
                    return;
                }
                setSMS(SMS => ({ ...SMS, tags: result }));
                SMS.tags = result;
            }

            const result = await ActivityService.setSMS({
                personid: person.PersonId,
                content: SMS.message ?? "",
                phone: SMS.telephone ?? "",
                activitytypeid: SMS.activityTypeId ? SMS.activityTypeId : undefined,
                tagids: SMS.tags,
                groupid: SMS.groupId || groupId,
            });
            if (result instanceof Error) {
                showToast(translate.ErrorProcessingRequest, undefined, "danger");
            }
            else {
                showToast(translate.Sent, undefined, "success");
                onSubmit && onSubmit();
                setSMS(new SMSEdit(person, activityTypeOptions.find(x => x)?.value));
            }
            setIsSending(false);
            FloatingPanelService.hidePanel();
        },
        [SMS, activityTypeOptions, canSubmit, groupId, isSending, onSubmit, person, requiredManager, showToast, translate.ErrorProcessingRequest, translate.Sent]);

    const insertFieldOptions = useMemo(() => {
        const clientFieldsMap = [
            TranslationService.translate.Client,
            TranslationService.translate.Contact,
            ...CompanyService.getAdditionalDefinitionsFiltered(Entities.Client).map(x => new EntityField(x)).map(x => x.title),
            TranslationService.translate.FollowUpMail,
        ].map(x => ({ text: x!, value: `[${x!}]` }));
        if (CompanyService.getCompanyAuthorization().includes("customerportalenabled")) {
            clientFieldsMap.push({ text: TranslationService.translate.CustomerSite, value: "[Portal]" });
        }
        const fields: DropdownItems<string> = [
            { isTitle: true, text: TranslationService.translate.FilterData.replace("{EntityName}", TranslationService.translate.Client) },
            ...clientFieldsMap,
        ];
        return fields;
    }, []);

    const insertTextInEditor = async (text?: string) => {
        if (!text) return;

        const currentState = currentEditorState.current!;
        const selection = currentState.getSelection();

        const contentWithText = Modifier.insertText(
            currentState.getCurrentContent(),
            selection,
            text
        );

        const newEditorState = EditorState.push(
            currentState,
            contentWithText,
            'insert-characters'
        );

        const endSelection = contentWithText.getSelectionAfter();
        const finalState = EditorState.forceSelection(newEditorState, endSelection);

        currentEditorState.current = finalState;
        editorRef.current!.focusEditor();
        editorRef.current!.onChange(finalState);

        const markup = draftToHtml(convertToRaw(finalState.getCurrentContent()));
        changeBody(markup);
    };

    return (
        <>
            <div className="tabBody">
                <div className="mb-2 g-0 col">
                    <div className="d-flex align-items-center mb-1">
                        <div className="col-2">
                            <label className="form-label mb-0">{translate.Telephone}</label>
                        </div>
                        <div className="d-flex align-items-center col gap-1">
                            {PhoneItems.length > 1 ? <div className="d-flex align-items-center col-5">
                                <Dropdown selectClasses="col" items={PhoneItems} onChange={reqTelephone} defaultValue={SMS.telephone ?? -1} value={SMS.telephone} />
                            </div> : null}
                            <div className={"d-flex input-column col"}>
                                <div><TextEditor onChange={reqTelephone} value={SMS.telephone} defaultValue={SMS.telephone} /></div>
                                <ValidationMessage onChange={reqTelephone} defaultValue={SMS.telephone} />
                            </div>
                        </div>
                    </div>

                    {hasValuesLength(activityTypeOptions) &&
                        <div className="d-flex align-items-center mb-1">
                            <div className="col-2">
                                <label className="form-label mb-0">{translate.ActivityType}</label></div>
                            <div className="col">
                                <Dropdown onChange={setSMSField("activityTypeId")} items={activityTypeOptions} optionLabel={translate.None} defaultValue={SMS.activityTypeId} />
                            </div>
                        </div>}

                    {hasValuesLength([insertFieldOptions]) &&
                        <div className="d-flex align-items-center ">
                            <div className="col-2">
                                <label className="form-label mb-0">{translate.InsertField}</label></div>
                            <div className="col">
                                <Dropdown onChange={insertTextInEditor} items={insertFieldOptions}
                                    value={undefined}
                                    placeholder={TranslationService.translate.InsertField} />
                            </div>
                        </div>}

                </div>
                <SMSEditor
                    defaultEditorState={currentEditorState.current}
                    onEditorStateChange={(editorState) => {
                        const plainText = editorState.getCurrentContent().getPlainText();

                        changeBody(plainText); // ahora guarda solo texto plano
                        setMessageCharCount(plainText.length); // cuenta caracteres correctamente

                        currentEditorState.current = editorState;
                    }}
                    locale={TranslationService.currentLanguage}
                    toolbarHidden
                    ref={x => editorRef.current = Cast<EditorType>(x)}
                />

                {!CompanyService.getSetting('unlimitedsms') &&
                    <label className={"form-label" + (messageCharCount > maxCharCount ? " text-danger" : "")}>
                        {messageCharCount}/{maxCharCount}
                    </label>}

                <div className="col-12">
                    <hr />
                </div>
                {Boolean(CompanyService.getTags()?.length) &&
                    <div className="d-flex align-items-center mb-1">
                        <div style={{ width: 45, textAlign: 'center' }}>
                            <TooltipComponent title={TranslationService.translate.Tags}>
                                <i className="fa fa-tags activity-icon-color my-2" onClick={() => { }} />
                            </TooltipComponent>
                        </div>
                        <div className="col">
                            <TagsDropdownEditor onChange={setSMSField("tags")} />
                        </div>
                    </div>}

                {CompanyService.getGroupName()?.length > 0 &&
                    <div className="d-flex align-items-center mb-2">
                        <div className="col-2">
                            <label className="form-label mb-0">{CompanyService.getGroupName()}</label></div>
                        <div className="col">
                            <AutocompleteSelect
                                loadOptions={value => GroupService.getGroupSelectOptions(person.PersonId.toString(), value)}
                                onChange={x => setSMSField("groupId")(x?.value)}
                                defaultVal={(groupLabel && groupId) ? { value: groupId!.toString(), label: groupLabel ? groupLabel as string : groupId!.toString() } : undefined}
                            />
                        </div>
                    </div>}
            </div >
            <FloatingPanelFooter>
                <button className='btn btn-primary me-3' onClick={submit}>
                    {translate.Send}
                    {isSending && <i className="fas fa-spinner-third fa-spin third ms-2"></i>}
                </button>
            </FloatingPanelFooter>
        </>
    );
};